import { useEffect, useRef } from "react";
import type { RefObject } from "react";

/** Selector of controls marked invalid, in document order. */
const invalidSelector = '[aria-invalid="true"]';

/** Selector of an error summary that receives the focus when no single control is invalid. */
const errorSummarySelector = "[data-error-summary]";

/**
 * Returns a ref whose element receives the focus once when it is mounted, for example the first
 * field of an inline form that just opened or an inline confirmation (WCAG 2.4.3, UX-110). An
 * element that is not focusable by itself (a heading or a container) needs `tabIndex={-1}`.
 *
 * @returns The ref to attach to the element.
 */
export function useFocusOnMount<T extends HTMLElement>(): RefObject<T | null> {
  const ref = useRef<T>(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  return ref;
}

/**
 * Moves the focus to the first invalid control (`aria-invalid="true"`) inside a container, or,
 * without one, to its error summary (`data-error-summary`, with `tabIndex={-1}`), so a person who
 * submitted a form lands on the first problem (F-3, WCAG 3.3.1, UX-110).
 *
 * @param container - The form or section to search; nothing happens for `null`.
 * @returns `true` if an element received the focus.
 */
export function focusFirstInvalid(container: HTMLElement | null): boolean {
  const target =
    container?.querySelector<HTMLElement>(invalidSelector) ??
    container?.querySelector<HTMLElement>(errorSummarySelector) ??
    null;

  target?.focus();

  const focused = target !== null;

  return focused;
}

/**
 * After every failed submit, moves the focus to the first invalid control of the container once the
 * errors are rendered (see {@link focusFirstInvalid}). Count the failed submits in state and pass
 * the count; `0` (no failed submit yet) moves nothing.
 *
 * @param container - Ref of the form or section.
 * @param failedSubmits - Number of failed submits so far.
 */
export function useFocusFirstInvalid(
  container: RefObject<HTMLElement | null>,
  failedSubmits: number,
): void {
  useEffect(() => {
    if (failedSubmits > 0) {
      focusFirstInvalid(container.current);
    }
  }, [container, failedSubmits]);
}
